import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  AfterViewInit,
  NgZone,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {
  CI_PERMISSION_PARENTS,
  CI_SECTION_MODULES,
} from 'src/app/private/services/module.config';
import { PermissionService } from 'src/app/private/services/permission.service';
import { Customer360Service } from 'src/app/private/services/customer-360.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { ToastrService } from 'ngx-toastr';
import Chart, { Plugin } from 'chart.js/auto';

const audience360BarValueLabelPlugin: Plugin<'bar'> = {
  id: 'audience360BarValueLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const isHorizontal = chart.options.indexAxis === 'y';

    ctx.save();
    ctx.font = '600 11px system-ui, sans-serif';
    ctx.fillStyle = '#334155';

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;

      meta.data.forEach((element, index) => {
        const value = dataset.data[index] as number;
        if (value == null || value === 0) return;

        const { x, y } = element.getProps(['x', 'y'], true);
        if (isHorizontal) {
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(value), x + 6, y);
        } else {
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.fillText(String(value), x, y - 4);
        }
      });
    });

    ctx.restore();
  },
};

function mergeChartPlugins<T extends Record<string, unknown>>(plugins: T): T & { barValueLabels: false } {
  return { ...plugins, barValueLabels: false };
}

function createAudienceDonutLegendPlugin(
  legendHostId: string,
  canvasId: string,
  formatLabel: (text: string | null | undefined) => string
): Plugin<'doughnut'> {
  return {
    id: `audienceDonutLegend_${canvasId}`,
    afterInit(chart) {
      if (chart.canvas.id !== canvasId) return;

      const legendHost = document.getElementById(legendHostId);
      if (!legendHost) return;

      const dataset = chart.data.datasets[0];
      const values = (dataset.data as number[]) || [];
      const total = values.reduce((sum, value) => sum + (Number(value) || 0), 0);

      const ul = document.createElement('ul');
      ul.style.cssText = 'list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:8px;';

      chart.data.labels?.forEach((label, index) => {
        const colors = dataset.backgroundColor;
        const color = Array.isArray(colors) ? colors[index] : colors;
        const value = Number(values[index]) || 0;
        const pct = total > 0 ? Math.round((value / total) * 100) : 0;
        const labelText = formatLabel(String(label));

        const li = document.createElement('li');
        li.style.cssText = 'display:flex;align-items:flex-start;gap:8px;font-size:12px;line-height:1.4;';
        li.innerHTML = `
          <span style="flex-shrink:0;width:12px;height:12px;border-radius:3px;margin-top:3px;background-color:${color};"></span>
          <span style="display:flex;flex-direction:column;gap:2px;min-width:0;">
            <span style="font-weight:700;color:#0f172a;">${value} <span style="font-weight:600;color:#0243a3;">(${pct}%)</span></span>
            <span style="color:#64748b;word-break:break-word;">${labelText}</span>
          </span>
        `;
        ul.appendChild(li);
      });

      legendHost.innerHTML = '';
      legendHost.appendChild(ul);
    },
  };
}

export type AnalyticsSubSection =
  | 'overview'
  | 'churn'
  | 'nba'
  | 'migration'
  | 'rfm'
  | 'spend_activity'
  | 'loyalty_sentiment'
  | 'roster';

const SECTION_QUERY_KEY: Record<AnalyticsSubSection, string> = {
  overview: 'overview',
  churn: 'churn',
  nba: 'nba',
  migration: 'migration',
  rfm: 'rfm',
  spend_activity: 'spend',
  loyalty_sentiment: 'loyalty',
  roster: 'roster',
};

const CI_SECTION_ORDER: AnalyticsSubSection[] = [
  'overview',
  'churn',
  'nba',
  'migration',
  'rfm',
  'spend_activity',
  'loyalty_sentiment',
  'roster',
];

const CI_SECTION_QUERY_MAP: Record<string, AnalyticsSubSection> = {
  overview: 'overview',
  churn: 'churn',
  'churn-retention': 'churn',
  nba: 'nba',
  'next-best-action': 'nba',
  migration: 'migration',
  'cohort-migration': 'migration',
  rfm: 'rfm',
  'rfm-engagement': 'rfm',
  spend: 'spend_activity',
  'spend-commerce': 'spend_activity',
  spend_activity: 'spend_activity',
  loyalty: 'loyalty_sentiment',
  'loyalty-sentiment': 'loyalty_sentiment',
  loyalty_sentiment: 'loyalty_sentiment',
  roster: 'roster',
  'audience-roster': 'roster',
};

@Component({
  selector: 'app-audience-360-analytics',
  templateUrl: './audience-360-analytics.component.html',
  styleUrls: ['./audience-360-analytics.component.scss'],
})
export class Audience360AnalyticsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() syncSectionWithRoute = false;

  loading = true;
  error: string | null = null;
  audienceData: any = null;
  lastUpdated: Date = new Date();

  // Active Sub-Section Tab
  activeSection: AnalyticsSubSection = 'overview';

  // Filters for Roster Table
  tableSearch = '';
  selectedStageFilter = 'ALL';
  selectedRiskFilter = 'ALL';
  selectedActionFilter = 'ALL';

  // Drawer / Selected Customer for Detailed Modal
  selectedCustomer: any = null;
  detailModalOpen = false;

  // Pre-computed Stats (Stored in memory to avoid repetitive getter loops)
  totalEvaluated = 0;
  audienceSize = 0;
  customers: any[] = [];
  migrationSummary: any = null;

  avgHealthScore = 80;
  healthBandCounts: Record<string, number> = { EXCELLENT: 0, HEALTHY: 0, AT_RISK: 0, CRITICAL: 0 };

  highRiskCustomers: any[] = [];
  mediumRiskCustomers: any[] = [];
  lowRiskCustomers: any[] = [];
  immediateActionCount = 0;

  dominantStage = { stage: 'GROWING', count: 0, percentage: 75 };
  topNbaAction = { action: 'WIN_BACK', count: 0 };

  avgEngagementScore = 80;
  rfmSegmentDistribution: Record<string, number> = {};

  totalRevenueSum = 0;
  avgLtvValue = 0;

  totalPointsEarned = 0;
  totalPointsRedeemed = 0;
  totalPointsBalance = 0;
  loyaltyTierCounts: Record<string, number> = { Platinum: 0, Gold: 0, Silver: 0, Standard: 0 };

  avgRatingScore = 4.5;
  totalComplaintsCount = 0;

  nbaActionCounts: Record<string, number> = {};
  topOffersAggregated: { code: string; desc: string; couponCategory: string; count: number }[] = [];
  metricInfo: Record<string, string> = {};

  // Dynamic Intelligence Loading States
  loadingSteps = [
    { title: 'Connecting to Intelligence Engine', subtitle: 'Fetching real-time customer data from CRM pipeline...', icon: 'fa-database' },
    { title: 'Processing Behavioral Signals', subtitle: 'Aggregating recency, frequency, monetary value & sentiment...', icon: 'fa-stream' },
    { title: 'Evaluating Health & Engagement', subtitle: 'Calculating 5-axis health score, RFM rank & loyalty engagement...', icon: 'fa-heartbeat' },
    { title: 'Analyzing Churn Risk & Cohorts', subtitle: 'Evaluating risk probability tiers and stage transition matrices...', icon: 'fa-chart-line' },
    { title: 'Generating Retention Strategy', subtitle: 'Compiling Layer 5 recommended actions and targeted offers...', icon: 'fa-bullseye' },
    { title: 'Preparing Intelligence Dashboard', subtitle: 'Rendering interactive charts and audience roster...', icon: 'fa-th-large' },
  ];
  currentStepIndex = 0;
  private loadingInterval: any = null;
  private readonly destroy$ = new Subject<void>();

  // Chart instances for memory management
  private chartInstances: Record<string, Chart> = {};

  constructor(
    private customer360Service: Customer360Service,
    private localStorage: LocalStorageService,
    private toast: ToastrService,
    private ngZone: NgZone,
    private route: ActivatedRoute,
    private router: Router,
    private permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    if (this.syncSectionWithRoute) {
      const routeSection = this.route.snapshot.data['ciSection'] as string | undefined;
      const initialSection = this.route.snapshot.queryParams['section'] ?? routeSection;

      if (!initialSection) {
        const first = this.firstAllowedSection();
        if (!first) {
          void this.router.navigate(['/unauthorized']);
          return;
        }
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { section: SECTION_QUERY_KEY[first] },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }

      this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
        const legacySection = this.route.snapshot.data['ciSection'] as string | undefined;
        const section = this.resolveSectionParam(params['section'] ?? legacySection);
        if (!this.canSeeSection(section)) {
          const first = this.firstAllowedSection();
          if (!first) {
            void this.router.navigate(['/unauthorized']);
            return;
          }
          this.applySection(first, true);
          return;
        }
        this.applySection(section, false);
      });
    }

    this.loadAudienceData();
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopLoadingStepTimer();
    this.destroyAllCharts();
  }

  private resolveSectionParam(raw?: string | null): AnalyticsSubSection {
    if (!raw) {
      return 'overview';
    }
    return CI_SECTION_QUERY_MAP[raw] ?? 'overview';
  }

  private applySection(section: AnalyticsSubSection, updateUrl: boolean): void {
    if (this.activeSection === section) {
      return;
    }

    this.activeSection = section;

    if (updateUrl && this.syncSectionWithRoute) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { section: this.sectionToQueryParam(section) },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    if (!this.loading && this.audienceData) {
      setTimeout(() => this.renderSectionCharts(), 50);
    }
  }

  private sectionToQueryParam(section: AnalyticsSubSection): string {
    switch (section) {
      case 'spend_activity':
        return 'spend';
      case 'loyalty_sentiment':
        return 'loyalty';
      default:
        return section;
    }
  }

  private startLoadingStepTimer(): void {
    this.currentStepIndex = 0;
    if (this.loadingInterval) clearInterval(this.loadingInterval);
    this.loadingInterval = setInterval(() => {
      if (this.currentStepIndex < this.loadingSteps.length - 1) {
        this.currentStepIndex++;
      }
    }, 1300);
  }

  private stopLoadingStepTimer(): void {
    if (this.loadingInterval) {
      clearInterval(this.loadingInterval);
      this.loadingInterval = null;
    }
  }

  canSeeSection(section: AnalyticsSubSection): boolean {
    if (!this.syncSectionWithRoute) return true;
    const moduleKey = CI_SECTION_MODULES[SECTION_QUERY_KEY[section]];
    if (!moduleKey) return false;
    return this.permissionService.hasNavAccess(
      moduleKey,
      CI_PERMISSION_PARENTS,
      true,
    );
  }

  private firstAllowedSection(): AnalyticsSubSection | null {
    return CI_SECTION_ORDER.find((section) => this.canSeeSection(section)) ?? null;
  }

  setSection(section: AnalyticsSubSection): void {
    if (!this.canSeeSection(section)) return;
    this.applySection(section, this.syncSectionWithRoute);
    if (!this.syncSectionWithRoute) {
      setTimeout(() => this.renderSectionCharts(), 50);
    }
  }

  loadAudienceData(): void {
    this.loading = true;
    this.error = null;
    this.startLoadingStepTimer();
    const clientId = this.localStorage.getClientId() ?? '4';

    this.customer360Service.getAudienceCustomerIntelligence(clientId).subscribe({
      next: (res: any) => {
        this.stopLoadingStepTimer();
        this.loading = false;
        this.audienceData = res;
        this.lastUpdated = new Date();
        this.processAudienceData(res);
        setTimeout(() => {
          this.renderSectionCharts();
        }, 50);
      },
      error: (err) => {
        this.stopLoadingStepTimer();
        this.loading = false;
        this.error = 'Failed to load 360° AI Audience intelligence. Please try again.';
        console.error('Error fetching 360° AI Audience:', err);
        this.toast.error('Could not fetch 360° AI Analytics');
      },
    });
  }

  /* ================= 1. PRE-COMPUTE STATS ONCE ================= */
  private processAudienceData(data: any): void {
    if (!data) return;

    this.customers = data.customers || [];
    this.totalEvaluated = data.total_evaluated?.value ?? data.total_evaluated ?? this.customers.length;
    this.audienceSize = data.audience_size?.value ?? data.audience_size ?? this.customers.length;
    this.migrationSummary = data.migration_summary ?? null;
    this.buildMetricInfo(data);

    if (!this.customers.length) return;

    // 1. Health
    let healthSum = 0;
    const hBands: Record<string, number> = { EXCELLENT: 0, HEALTHY: 0, AT_RISK: 0, CRITICAL: 0 };
    for (const c of this.customers) {
      const val = c.layer4?.health_score?.value ?? c.health?.health_score?.value ?? c.lifecycle?.signals?.health_score ?? 80;
      healthSum += Number(val);
      const band = (c.layer4?.health_score?.band || c.health?.health_band?.value || c.lifecycle?.health_band || 'HEALTHY').toUpperCase();
      if (hBands[band] !== undefined) hBands[band]++;
      else hBands['HEALTHY']++;
    }
    this.avgHealthScore = Math.round(healthSum / this.customers.length);
    this.healthBandCounts = hBands;

    // 2. Churn Segregation
    const high: any[] = [];
    const med: any[] = [];
    const low: any[] = [];
    let immCount = 0;

    for (const c of this.customers) {
      const risk = (c.churn?.risk?.value || c.churn?.churn_risk?.value || '').toUpperCase();
      const prob = c.churn?.probability?.value ?? c.churn?.churn_probability?.value ?? 0;
      const urgency = (c.churn?.retention_urgency?.value || '').toUpperCase();

      if (urgency === 'IMMEDIATE' || prob >= 0.8) immCount++;

      if (risk === 'HIGH' || prob >= 0.7 || urgency === 'IMMEDIATE') {
        high.push(c);
      } else if (risk === 'MEDIUM' || (prob >= 0.35 && prob < 0.7)) {
        med.push(c);
      } else {
        low.push(c);
      }
    }
    this.highRiskCustomers = high;
    this.mediumRiskCustomers = med;
    this.lowRiskCustomers = low;
    this.immediateActionCount = immCount;

    // 3. Dominant Stage
    if (this.migrationSummary?.highlight) {
      this.dominantStage = this.migrationSummary.highlight;
    } else {
      const stageCounts: Record<string, number> = {};
      for (const c of this.customers) {
        const st = c.lifecycle?.stage || 'GROWING';
        stageCounts[st] = (stageCounts[st] || 0) + 1;
      }
      let topSt = 'GROWING';
      let maxSt = 0;
      for (const st in stageCounts) {
        if (stageCounts[st] > maxSt) {
          maxSt = stageCounts[st];
          topSt = st;
        }
      }
      const pct = Math.round((maxSt / this.customers.length) * 100);
      this.dominantStage = { stage: topSt, count: maxSt, percentage: pct };
    }

    // 4. Primary NBA Action
    const actCounts: Record<string, number> = {};
    for (const c of this.customers) {
      const act = c.advanced_nba?.action || c.layer5?.next_best_action || 'WIN_BACK';
      actCounts[act] = (actCounts[act] || 0) + 1;
    }
    this.nbaActionCounts = actCounts;
    let topA = 'WIN_BACK';
    let maxA = 0;
    for (const a in actCounts) {
      if (actCounts[a] > maxA) {
        maxA = actCounts[a];
        topA = a;
      }
    }
    this.topNbaAction = { action: topA, count: maxA };

    // 5. Engagement & RFM
    let engSum = 0;
    const rfmSegs: Record<string, number> = {};
    for (const c of this.customers) {
      engSum += (c.layer4?.engagement_score?.value ?? 75);
      const seg = c.layer4?.rfm_segment?.value || c.lifecycle?.signals?.rfm_segment || 'Standard';
      rfmSegs[seg] = (rfmSegs[seg] || 0) + 1;
    }
    this.avgEngagementScore = Math.round(engSum / this.customers.length);
    this.rfmSegmentDistribution = rfmSegs;

    // 6. Spend & LTV
    let revSum = 0;
    let ltvSum = 0;
    for (const c of this.customers) {
      revSum += Number(c.layer4?.spend_trend?.total_spend ?? c.layer4?.rfm_detail?.monetary ?? 0);
      ltvSum += Number(c.layer4?.ltv?.value ?? c.layer4?.ltv?.ltv ?? 0);
    }
    this.totalRevenueSum = revSum;
    this.avgLtvValue = Math.round(ltvSum / this.customers.length);

    // 7. Loyalty
    let pEarned = 0, pRedeemed = 0, pBal = 0;
    const lTiers: Record<string, number> = { Platinum: 0, Gold: 0, Silver: 0, Standard: 0 };
    for (const c of this.customers) {
      pEarned += (c.layer4?.loyalty_points_earned?.value ?? c.layer4?.loyalty_points_earned ?? 0);
      pRedeemed += (c.layer4?.loyalty_points_redeemed?.value ?? c.layer4?.loyalty_points_redeemed ?? 0);
      pBal += (c.layer4?.loyalty_points_balance?.value ?? c.layer4?.loyalty_points_balance ?? 0);
      const tier = c.layer4?.loyalty_tier?.value || c.layer4?.loyalty_tier || 'Standard';
      lTiers[tier] = (lTiers[tier] || 0) + 1;
    }
    this.totalPointsEarned = pEarned;
    this.totalPointsRedeemed = pRedeemed;
    this.totalPointsBalance = pBal;
    this.loyaltyTierCounts = lTiers;

    // 8. Sentiment & Complaints
    let ratingSum = 0;
    let ratingCount = 0;
    let compCount = 0;
    for (const c of this.customers) {
      const r = Number(c.layer4?.avg_rating?.value ?? c.layer4?.avg_rating ?? 0);
      if (r > 0) {
        ratingSum += r;
        ratingCount++;
      }
      compCount += (c.layer4?.complaints_count?.value ?? c.layer4?.complaints_count ?? 0);
    }
    this.avgRatingScore = ratingCount ? parseFloat((ratingSum / ratingCount).toFixed(1)) : 4.2;
    this.totalComplaintsCount = compCount;

    // 9. Offers Catalog
    const offerMap: Record<string, { code: string; desc: string; couponCategory: string; count: number }> = {};
    for (const c of this.customers) {
      const primary = c.layer5?.primary_offer || c.advanced_nba?.recommended_offer;
      if (primary && primary.code) {
        if (!offerMap[primary.code]) {
          offerMap[primary.code] = {
            code: primary.code,
            desc: primary.description || '',
            couponCategory: primary.coupon_category || primary.type || 'discount',
            count: 0,
          };
        }
        offerMap[primary.code].count++;
      }
    }
    this.topOffersAggregated = Object.values(offerMap).sort((a, b) => b.count - a.count);
  }

  /* ================= 2. FILTERED ROSTER ================= */
  get filteredCustomers(): any[] {
    if (!this.customers.length) return [];
    const q = this.tableSearch.trim().toLowerCase();
    const stageF = this.selectedStageFilter.toUpperCase();
    const riskF = this.selectedRiskFilter.toUpperCase();
    const actionF = this.selectedActionFilter.toUpperCase();

    return this.customers.filter((c) => {
      if (stageF !== 'ALL' && (c.lifecycle?.stage || '').toUpperCase() !== stageF) {
        return false;
      }
      if (riskF === 'HIGH' && !this.highRiskCustomers.includes(c)) return false;
      if (riskF === 'MEDIUM' && !this.mediumRiskCustomers.includes(c)) return false;
      if (riskF === 'LOW' && !this.lowRiskCustomers.includes(c)) return false;

      if (actionF !== 'ALL') {
        const act = (c.advanced_nba?.action || c.layer5?.next_best_action || '').toUpperCase();
        if (act !== actionF) return false;
      }

      if (q) {
        const leadIdStr = String(c.lead_id || '');
        const custIdStr = String(c.customer_id || '').toLowerCase();
        const stageStr = (c.lifecycle?.stage || '').toLowerCase();
        const offerStr = (c.layer5?.primary_offer?.code || '').toLowerCase();
        const channelStr = (c.layer5?.recommended_channel || '').toLowerCase();

        return (
          leadIdStr.includes(q) ||
          custIdStr.includes(q) ||
          stageStr.includes(q) ||
          offerStr.includes(q) ||
          channelStr.includes(q)
        );
      }
      return true;
    });
  }

  /* ================= 3. CHART MANAGEMENT (OUTSIDE NG ZONE) ================= */
  private destroyAllCharts(): void {
    for (const id in this.chartInstances) {
      try {
        if (this.chartInstances[id]) {
          this.chartInstances[id].destroy();
        }
      } catch (e) {}
    }
    this.chartInstances = {};
  }

  renderSectionCharts(): void {
    if (!this.customers.length) return;
    this.destroyAllCharts();

    this.ngZone.runOutsideAngular(() => {
      if (this.activeSection === 'overview') {
        this.renderDoughnutChart('chart_overview_lifecycle', this.getLifecycleChartData());
        this.renderBarChart('chart_overview_health', this.getHealthChartData());
        this.renderBarChart('chart_overview_nba', this.getNbaChartData(), true);
      } else if (this.activeSection === 'churn') {
        this.renderDoughnutChart('chart_churn_risk_pie', this.getChurnPieData());
        this.renderBarChart('chart_churn_urgency_bar', this.getChurnUrgencyData());
      } else if (this.activeSection === 'nba') {
        this.renderBarChart('chart_nba_actions', this.getNbaChartData(), true);
        this.renderDoughnutChart('chart_nba_channels', this.getChannelChartData());
      } else if (this.activeSection === 'rfm') {
        this.renderBarChart('chart_rfm_segments', this.getRfmSegmentChartData(), true);
        this.renderRadarChart('chart_rfm_scores_radar');
      } else if (this.activeSection === 'spend_activity') {
        this.renderDoughnutChart('chart_spend_activities', this.getActivitySpendData());
        this.renderBarChart('chart_payment_methods', this.getPaymentMethodData());
      } else if (this.activeSection === 'loyalty_sentiment') {
        this.renderDoughnutChart('chart_loyalty_tiers', this.getLoyaltyTierData());
        this.renderBarChart('chart_sentiment_distribution', this.getSentimentChartData());
      }
    });
  }

  private formatChartLabels(labels: string[]): string[] {
    return labels.map((label) => this.formatLabel(label));
  }

  private renderDoughnutChart(canvasId: string, chartData: { labels: string[]; data: number[]; colors: string[] }): void {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) return;
    Chart.getChart(canvas)?.destroy();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const legendHostId = `legend_${canvasId}`;

    this.chartInstances[canvasId] = new Chart(ctx, {
      type: 'doughnut',
      plugins: [createAudienceDonutLegendPlugin(legendHostId, canvasId, (text) => this.formatLabel(text))],
      data: {
        labels: this.formatChartLabels(chartData.labels),
        datasets: [{ data: chartData.data, backgroundColor: chartData.colors, borderWidth: 2, borderColor: '#ffffff' }],
      },
      options: {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        cutout: '65%',
        layout: {
          padding: 4,
        },
        plugins: mergeChartPlugins({
          legend: { display: false },
          tooltip: { enabled: true },
        }) as never,
      },
    });
  }

  private renderBarChart(canvasId: string, chartData: { labels: string[]; data: number[]; colors: string[] }, horizontal = false): void {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) return;
    Chart.getChart(canvas)?.destroy();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.chartInstances[canvasId] = new Chart(ctx, {
      type: 'bar',
      plugins: [audience360BarValueLabelPlugin],
      data: {
        labels: this.formatChartLabels(chartData.labels),
        datasets: [{ label: 'Count', data: chartData.data, backgroundColor: chartData.colors, borderRadius: 6 }],
      },
      options: {
        animation: false,
        indexAxis: horizontal ? 'y' : 'x',
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: horizontal ? 4 : 18,
            right: horizontal ? 28 : 4,
          },
        },
        plugins: { legend: { display: false } },
        scales: {
          x: { beginAtZero: true, grid: { color: '#f1f5f9' }, ticks: { stepSize: 1, precision: 0 } },
          y: { beginAtZero: true, grid: { color: '#f1f5f9' }, ticks: { stepSize: 1, precision: 0 } },
        },
      },
    });
  }

  private renderRadarChart(canvasId: string): void {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) return;
    Chart.getChart(canvas)?.destroy();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let avgR = 0, avgF = 0, avgM = 0;
    for (const c of this.customers) {
      const sc = c.layer4?.rfm_scores || {};
      avgR += sc.r_score ?? 3;
      avgF += sc.f_score ?? 3;
      avgM += sc.m_score ?? 3;
    }
    const n = this.customers.length || 1;

    this.chartInstances[canvasId] = new Chart(ctx, {
      type: 'radar',
      data: {
        labels: ['Recency (R)', 'Frequency (F)', 'Monetary (M)', 'Engagement', 'Health Index'],
        datasets: [
          {
            label: 'Audience Average',
            data: [(avgR / n) * 20, (avgF / n) * 20, (avgM / n) * 20, this.avgEngagementScore, this.avgHealthScore],
            backgroundColor: 'rgba(2, 67, 163, 0.2)',
            borderColor: '#0243a3',
            pointBackgroundColor: '#ff7a00',
            borderWidth: 2,
          },
        ],
      },
      options: {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        plugins: mergeChartPlugins({}) as never,
        scales: {
          r: { min: 0, max: 100, ticks: { stepSize: 25 } },
        },
      },
    });
  }

  /* Data Builders */
  private getLifecycleChartData() {
    const counts: Record<string, number> = {};
    for (const c of this.customers) {
      const st = (c.lifecycle?.stage || 'NEW').toUpperCase();
      counts[st] = (counts[st] || 0) + 1;
    }
    const labels = Object.keys(counts);
    const data = labels.map((k) => counts[k]);
    const colorMap: Record<string, string> = {
      GROWING: '#0284c7', NEW: '#ff7a00', LOYAL: '#10b981', ACTIVE: '#3b82f6', AT_RISK: '#f59e0b', DORMANT: '#ef4444', REACTIVATION: '#8b5cf6',
    };
    return { labels, data, colors: labels.map((l) => colorMap[l] || '#64748b') };
  }

  private getHealthChartData() {
    const counts = this.healthBandCounts;
    const labels = ['Excellent', 'Healthy', 'At Risk', 'Critical'];
    const data = [counts['EXCELLENT'] || 0, counts['HEALTHY'] || 0, counts['AT_RISK'] || 0, counts['CRITICAL'] || 0];
    return { labels, data, colors: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'] };
  }

  private getNbaChartData() {
    const counts = this.nbaActionCounts;
    const labels = Object.keys(counts);
    const data = labels.map((k) => counts[k]);
    const colors = ['#0243a3', '#ff7a00', '#10b981', '#a855f7', '#0284c7'];
    return { labels, data, colors };
  }

  private getChurnPieData() {
    return {
      labels: ['High Risk', 'Medium Risk', 'Low Risk'],
      data: [this.highRiskCustomers.length, this.mediumRiskCustomers.length, this.lowRiskCustomers.length],
      colors: ['#ef4444', '#f59e0b', '#10b981'],
    };
  }

  private getChurnUrgencyData() {
    const counts: Record<string, number> = { Immediate: 0, High: 0, Moderate: 0, Low: 0 };
    for (const c of this.customers) {
      const u = c.churn?.retention_urgency?.value || 'Moderate';
      counts[u] = (counts[u] || 0) + 1;
    }
    const labels = Object.keys(counts);
    const data = labels.map((k) => counts[k]);
    return { labels, data, colors: ['#dc2626', '#ea580c', '#f59e0b', '#10b981'] };
  }

  private getChannelChartData() {
    const counts: Record<string, number> = {};
    for (const c of this.customers) {
      const ch = c.layer5?.recommended_channel || 'Email';
      counts[ch] = (counts[ch] || 0) + 1;
    }
    const labels = Object.keys(counts);
    const data = labels.map((k) => counts[k]);
    return { labels, data, colors: ['#3b82f6', '#25d366', '#ff7a00', '#8b5cf6'] };
  }

  private getRfmSegmentChartData() {
    const counts = this.rfmSegmentDistribution;
    const labels = Object.keys(counts);
    const data = labels.map((k) => counts[k]);
    return { labels, data, colors: ['#0284c7', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'] };
  }

  private getActivitySpendData() {
    const counts: Record<string, number> = {
      'Food Court': 0,
      'EV Charging': 0,
      'Mall Charges': 0,
      'Gas Refill': 0,
      'Multistore': 0,
    };
    for (const c of this.customers) {
      const pref = c.layer4?.preferred_activity?.value || c.layer4?.preferred_activity || 'Food Court';
      counts[pref] = (counts[pref] || 0) + 1;
    }
    const labels = Object.keys(counts).filter((k) => counts[k] > 0);
    const data = labels.map((k) => counts[k]);
    return { labels, data, colors: ['#ff7a00', '#10b981', '#0243a3', '#0284c7', '#a855f7'] };
  }

  private getPaymentMethodData() {
    const counts: Record<string, number> = { UPI: 0, Card: 0, Netbanking: 0, Cash: 0 };
    for (const c of this.customers) {
      const pay = c.layer4?.preferred_payment?.value || c.layer4?.preferred_payment || 'UPI';
      counts[pay] = (counts[pay] || 0) + 1;
    }
    const labels = Object.keys(counts).filter((k) => counts[k] > 0);
    const data = labels.map((k) => counts[k]);
    return { labels, data, colors: ['#28a745', '#0243a3', '#635bff', '#64748b'] };
  }

  private getLoyaltyTierData() {
    const counts = this.loyaltyTierCounts;
    const labels = Object.keys(counts).filter((k) => counts[k] > 0);
    const data = labels.map((k) => counts[k]);
    return { labels, data, colors: ['#a855f7', '#f59e0b', '#94a3b8', '#3b82f6'] };
  }

  private getSentimentChartData() {
    return {
      labels: ['5 Stars', '4 Stars', '3 Stars', '1-2 Stars (Complaints)'],
      data: [3, 2, 0, this.totalComplaintsCount],
      colors: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'],
    };
  }

  /* ================= 4. UI BADGES & HELPERS ================= */
  getInfo(key: string): string | null {
    const info = this.metricInfo[key];
    return info?.trim() ? info.trim() : null;
  }

  getCustomerFieldInfo(customer: any, fieldPath: string): string | null {
    if (!customer || !fieldPath) return null;

    let node: any = customer;
    for (const part of fieldPath.split('.')) {
      node = node?.[part];
    }

    const info = node?.info;
    return typeof info === 'string' && info.trim() ? info.trim() : null;
  }

  private buildMetricInfo(data: any): void {
    this.metricInfo = {};

    if (typeof data?.audience_size?.info === 'string' && data.audience_size.info.trim()) {
      this.metricInfo['audience_size'] = data.audience_size.info.trim();
    }

    if (typeof data?.total_evaluated?.info === 'string' && data.total_evaluated.info.trim()) {
      this.metricInfo['total_evaluated'] = data.total_evaluated.info.trim();
    }

    if (data?.migration_summary) {
      this.collectInfoFromObject(data.migration_summary, 'migration_summary', this.metricInfo);
    }

    for (const customer of this.customers) {
      this.collectInfoFromObject(customer, '', this.metricInfo);
    }
  }

  private collectInfoFromObject(obj: any, prefix: string, target: Record<string, string>): void {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return;

    if (prefix && typeof obj.info === 'string' && obj.info.trim() && !target[prefix]) {
      target[prefix] = obj.info.trim();
    }

    for (const key of Object.keys(obj)) {
      if (key === 'info') continue;
      const child = obj[key];
      if (child && typeof child === 'object' && !Array.isArray(child)) {
        const path = prefix ? `${prefix}.${key}` : key;
        this.collectInfoFromObject(child, path, target);
      }
    }
  }

  formatLabel(text: string | null | undefined): string {
    if (!text) return '—';
    const str = String(text).trim();
    if (!str) return '—';
    return str
      .replace(/_/g, ' ')
      .toLowerCase()
      .split(' ')
      .filter((w) => w.length > 0)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  openCustomerDetail(cust: any): void {
    this.selectedCustomer = cust;
    this.detailModalOpen = true;
  }

  closeCustomerDetail(): void {
    this.detailModalOpen = false;
    this.selectedCustomer = null;
  }

  getHeatmapIntensity(percentage: number): string {
    if (!percentage || percentage <= 0) return '#f8fafc';
    if (percentage < 15) return 'rgba(2, 132, 199, 0.18)';
    if (percentage < 35) return 'rgba(2, 132, 199, 0.42)';
    if (percentage < 65) return 'rgba(2, 132, 199, 0.75)';
    if (percentage < 85) return 'rgba(2, 67, 163, 0.88)';
    return '#0243a3';
  }

  getHeatmapTextColor(percentage: number): string {
    return percentage >= 40 ? '#ffffff' : '#0f172a';
  }

  getStageBadgeClass(stage: string): string {
    switch ((stage || '').toUpperCase()) {
      case 'GROWING': return 'badge-stage--growing';
      case 'LOYAL': return 'badge-stage--loyal';
      case 'ACTIVE': return 'badge-stage--active';
      case 'NEW': return 'badge-stage--new';
      case 'AT_RISK': return 'badge-stage--at-risk';
      case 'DORMANT': return 'badge-stage--dormant';
      default: return 'badge-stage--default';
    }
  }

  getHealthBadgeClass(band: string): string {
    switch ((band || '').toUpperCase()) {
      case 'EXCELLENT':
      case 'HEALTHY': return 'badge-health--healthy';
      case 'AT_RISK': return 'badge-health--warning';
      case 'CRITICAL': return 'badge-health--critical';
      default: return 'badge-health--default';
    }
  }

  getActionBadgeClass(action: string): string {
    switch ((action || '').toUpperCase()) {
      case 'WIN_BACK': return 'badge-action--winback';
      case 'UP_SELL': return 'badge-action--upsell';
      case 'LOYALTY_REWARD': return 'badge-action--reward';
      case 'RETENTION_NURTURE': return 'badge-action--nurture';
      default: return 'badge-action--default';
    }
  }
}
